import React, { useState } from 'react';
import {
    ButtonSecondary,
    DialogBox,
    ButtonPrimary,
    ButtonTertiary,
    DialogBoxHeader,
    DialogBoxBody
} from '@americanexpress/dls-react';

import {
    SPENDSMART_TERMS_AND_CONDITIONS,
    AMEX_PRIVACY_CENTER,
    DATA_CONNECTION_TERMS_AND_CONDITIONS
} from 'src/utils/constants';
import adobeTags from 'src/types/adobe-tags';
import { trackClick } from 'src/hooks/useAdobeTags';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { useUnenrollUser, useUser } from 'src/hooks/queries/users';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import Typography from 'src/components/typography/Typography';
import Selectors from 'src/utils/selectors';

import colors from 'src/styles/colors.module.scss';
import styles from './unenroll.module.scss';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';

export default function Unenroll({ className }: { className?: string }): JSX.Element {
    const { isLoading: isLoadingUser, data: user } = useUser();
    const { mutateAsync: unenrollUser } = useUnenrollUser();
    const [showAlert, setShowAlert] = useState(false);

    if (isLoadingUser) return <Loading />;

    const alert = (
        <DialogBox id="dialog-box" data-cy={Selectors.SpendSmart.UNENROLL_DIALOG} className={styles.dialogBox}>
            <DialogBoxHeader className={styles.gray06Text} id="dialog-box-header">
                {/* styles are coming from the DialogBoxHeader so no Typography */}
                <p>{LiteralsService.getLiteral('Budget.unenroll.alert.unenrollFromSpendSmart')}</p>
            </DialogBoxHeader>
            <DialogBoxBody className={`${styles.gray06Text} ${styles.dialogBody}`} id="dialog-box-body">
                {/* styles are coming from the DialogBoxBody so no Typography */}
                <p>{LiteralsService.getLiteral('Budget.unenroll.alert.areYouSure')}</p>
                <div className={styles.dialogActions}>
                    <ButtonPrimary
                        onClick={async () => {
                            trackClick(location.pathname, adobeTags.Unenroll.Confirm);
                            await unenrollUser(user);
                        }}
                    >
                        {LiteralsService.getLiteral('Budget.unenroll.unenroll')}
                    </ButtonPrimary>
                    <ButtonTertiary
                        onClick={() => {
                            setShowAlert(false);
                            trackClick(location.pathname, adobeTags.Unenroll.Cancel);
                        }}
                        data-cy={Selectors.General.UNENROLL_CANCEL_BUTTON}
                    >
                        {LiteralsService.getLiteral('Budget.unenroll.alert.cancel')}
                    </ButtonTertiary>
                </div>
            </DialogBoxBody>
        </DialogBox>
    );

    return (
        <>
            <div className={`${styles.unenrollSection} ${className}`}>
                <Typography
                    variant={TypographyVariants.HEADING_S}
                    className={colors.textStrong}
                    data-cy="unenrollSection"
                    component="h3"
                >
                    {LiteralsService.getLiteral('Budget.unenroll.unenrollFromSpendSmart')}
                </Typography>
                <Typography component="p" variant={TypographyVariants.BODY} className={colors.textWeak}>
                    {LiteralsService.getLiteral('Budget.unenroll.whenYouUnenroll')}
                    <Anchor href={SPENDSMART_TERMS_AND_CONDITIONS} inline tag={adobeTags.Unenroll.TC}>
                        {LiteralsService.getLiteral('Budget.termsAndConditions')}
                    </Anchor>
                    {LiteralsService.getLiteral('Budget.theAmex')}
                    <Anchor href={AMEX_PRIVACY_CENTER} inline tag={adobeTags.Unenroll.PrivacyCenter}>
                        {LiteralsService.getLiteral('Budget.privacyCenter')}
                    </Anchor>
                    {LiteralsService.getLiteral('Budget.andThe')}
                    <Anchor
                        href={DATA_CONNECTION_TERMS_AND_CONDITIONS}
                        inline
                        tag={adobeTags.Unenroll.DataConnectionTerms}
                    >
                        {LiteralsService.getLiteral('Budget.dataConnectionTermsAndConditionsLink')}
                    </Anchor>
                    .
                </Typography>
                <Typography component="p" variant={TypographyVariants.LEGAL_SECONDARY}>
                    {LiteralsService.getLiteral('Budget.unenroll.thisCannotBeUndone')}
                </Typography>
                {/* wrap button in div so that it shrinks to 180px width, DLS is weird */}
                <div>
                    <ButtonSecondary
                        onClick={() => {
                            setShowAlert(true);
                            trackClick(location.pathname, adobeTags.Unenroll.Button);
                        }}
                        data-cy={Selectors.General.UNENROLL_BUTTON}
                    >
                        {LiteralsService.getLiteral('Budget.unenroll.unenroll')}
                    </ButtonSecondary>
                </div>
            </div>
            {showAlert && alert}
        </>
    );
}
